/**
 * Step-level Additional Information card for repeatable instance editors (#1154).
 */

import React, { useMemo } from 'react';
import { StyleSheet } from 'react-native';

import { RequestFormAdditionalInformation } from '../../request/steps/subsections/AdditionalInformation';
import { spacing } from '../../theme';

import type { SavedAttachment } from '../../services/attachmentService';
import type { SubsectionFieldDescriptor } from '../../utils/subsectionHeaderMetrics';
import type { SelectedFile } from '../forms/FileUpload';

const EDITOR_SUBSECTION_PERSIST = {
  persistSectionExpanded: true,
} as const;

export interface RepeatableInstanceAdditionalInfoProps {
  titleSubtitle: string;
  sectionId: string;
  fieldId: string;
  inputLabel: string;
  placeholder: string;
  value: string;
  onChangeText: (value: string) => void;
  requestId?: string;
  requestStage: string;
  disabled: boolean;
  subsectionFields?: readonly SubsectionFieldDescriptor[];
  headerValues?: Record<string, unknown>;
  attachmentsBySection: Record<string, SavedAttachment[]>;
  onAttachFiles?: (sectionId: string) => void;
  onDeleteAttachment?: (attachmentId: number) => void;
  selectedFiles: Record<string, SelectedFile[]>;
  onFilesSelected?: (sectionId: string, files: SelectedFile[]) => void;
  offlineMode: boolean;
  onCaptionChange?: (attachmentId: number, caption: string) => void;
  pendingCaptionEdits?: Record<number, string>;
  /**
   * Optional dirty marker. Per-instance editors (Large UPS / Power Distribution)
   * update the note through `updateField`, which already tracks form dirtiness,
   * so they omit this. Shared-field editors (cooling / liquid cooling) still pass it.
   */
  markDirty?: () => void;
  showFullDetails?: boolean;
}

export const RepeatableInstanceAdditionalInfo: React.FC<
  RepeatableInstanceAdditionalInfoProps
> = ({
  titleSubtitle,
  sectionId,
  fieldId,
  inputLabel,
  placeholder,
  value,
  onChangeText,
  requestId,
  requestStage,
  disabled,
  subsectionFields,
  headerValues,
  attachmentsBySection,
  onAttachFiles,
  onDeleteAttachment,
  selectedFiles,
  onFilesSelected,
  offlineMode,
  onCaptionChange,
  pendingCaptionEdits,
  markDirty,
  showFullDetails = true,
}) => {
  const attachments = attachmentsBySection[sectionId] ?? [];
  const sectionSelectedFiles = selectedFiles[sectionId] ?? [];

  const onFilesSelectedDirty = useMemo(
    () =>
      onFilesSelected
        ? (files: SelectedFile[]) => {
            markDirty?.();
            onFilesSelected(sectionId, files);
          }
        : undefined,
    [markDirty, onFilesSelected, sectionId],
  );

  const onDeleteAttachmentDirty = useMemo(
    () =>
      onDeleteAttachment
        ? (attachmentId: number) => {
            markDirty?.();
            onDeleteAttachment(attachmentId);
          }
        : undefined,
    [markDirty, onDeleteAttachment],
  );

  const onCaptionChangeDirty = useMemo(
    () =>
      onCaptionChange
        ? (attachmentId: number, caption: string) => {
            markDirty?.();
            onCaptionChange(attachmentId, caption);
          }
        : undefined,
    [markDirty, onCaptionChange],
  );

  return (
    <RequestFormAdditionalInformation
      shell="stepForm"
      titleSubtitle={titleSubtitle}
      sectionId={sectionId}
      requestId={requestId}
      requestStage={requestStage}
      allowAttachments
      attachments={attachments}
      onAttachFiles={onAttachFiles ? () => onAttachFiles(sectionId) : undefined}
      onDeleteAttachment={onDeleteAttachmentDirty}
      selectedFiles={sectionSelectedFiles}
      onFilesSelected={onFilesSelectedDirty}
      isOffline={offlineMode}
      onCaptionChange={onCaptionChangeDirty}
      pendingCaptionEdits={pendingCaptionEdits}
      isEditing={!disabled}
      disabled={disabled}
      fieldId={fieldId}
      permissionRequestStage={requestStage}
      inputLabel={inputLabel}
      placeholder={placeholder}
      value={value}
      onChangeText={value => {
        markDirty?.();
        onChangeText(value);
      }}
      maxLength={10000}
      inputUseEditableProp={false}
      inputDisabled={disabled}
      wrapFieldInRowColumn={false}
      subsectionFields={subsectionFields}
      headerValues={headerValues}
      showFullDetails={showFullDetails}
      containerStyle={styles.sectionCard}
      defaultSectionExpanded={false}
      {...EDITOR_SUBSECTION_PERSIST}
    />
  );
};

const styles = StyleSheet.create({
  sectionCard: {
    marginBottom: spacing.md,
  },
});
